import api from './api';

// --- TypeScript Interfaces (matching backend fdb.service.ts response shapes) ---

export interface FdbAllergenPick {
  conceptId: number;
  conceptIdType: number;
  description: string;
  reverseLookupOnly: boolean;
}

export interface FdbConditionPick {
  fdbdx: string;
  fdbdxDesc: string;
  dxId: number;
}

export interface FdbCounselingMessage {
  code: number;
  importance: number;
  professionalMessage: string;
  patientMessage: string;
}

export interface FdbCounselingResponse {
  din: string;
  drugName: string;
  gcn: number;
  counselingMessages: FdbCounselingMessage[];
}

export interface FdbWarning {
  code: number;
  priority: string;
  text: string;
}

export interface FdbWarningsResponse {
  din: string;
  drugName: string;
  gcn: number;
  warnings: FdbWarning[];
}

export interface FdbSideEffect {
  description: string;
  frequency: string;
  severity: string;
  requiresLabMonitoring: boolean;
  requiresPhysicianNotification: boolean;
  isHypersensitivity: boolean;
}

export interface FdbSideEffectsResponse {
  din: string;
  drugName: string;
  gcn: number;
  sideEffects: FdbSideEffect[];
}

export interface FdbFoodInteraction {
  code: number;
  drugName: string;
  severityLevel: number;
  result: string;
  message: string;
  monograph: string;
}

export interface FdbFoodInteractionsResponse {
  din: string;
  drugName: string;
  gcn: number;
  foodInteractions: FdbFoodInteraction[];
}

export interface FdbGeriatricPrecaution {
  code: number;
  description: string;
  severityLevel: number;
  organSystems: {
    renal: string | null;
    hepatic: string | null;
    cardiovascular: string | null;
    pulmonary: string | null;
    neurologic: string | null;
    endocrine: string | null;
  };
  comment: string;
}

export interface FdbPediatricPrecaution {
  code: number;
  description: string;
  severityLevel: number;
  minAgeDays: number | null;
  maxAgeDays: number | null;
  narrative: string;
}

export interface FdbPregnancyPrecaution {
  code: number;
  description: string;
  severityLevel: number;
  severityLevelDescription: string;
  narrative: string;
}

export interface FdbLactationPrecaution {
  code: number;
  description: string;
  severityLevel: number;
  severityLevelDescription: string;
  excretionPotential: { code: string; description: string };
  effectsOnInfant: { code: string; description: string };
  narrative: string;
}

export interface FdbPrecautionsResponse {
  din: string;
  drugName: string;
  gcn: number;
  precautions: {
    geriatric: FdbGeriatricPrecaution[];
    pediatric: FdbPediatricPrecaution[];
    pregnancy: FdbPregnancyPrecaution[];
    lactation: FdbLactationPrecaution[];
  };
}

export interface FdbMonographSection {
  section: string;
  text: string;
}

export interface FdbMonograph {
  monographId: number;
  sections: FdbMonographSection[];
}

export interface FdbPatientEducationResponse {
  din: string;
  drugName: string;
  gcn?: number;
  monographs: FdbMonograph[];
}

// --- Drug Info ---

export interface FdbDrugInfo {
  din: string;
  gcn: number;
  brandName: string;
  labelName: string;
  genericName: string;
  strength: string;
  manufacturerDesc: string;
  dosageFormCode: string;
  routeOfAdministration: string;
  ahfsCode: string;
  ahfsDescription: string;
  drugCategoryCode: string;
  hic3: string;
  hiclSeqn: string;
  tallman: string;
  discontinuedDate: string | null;
}

// --- Indications ---

export interface FdbIndication {
  code: string;
  labelCode: string;
  drugDescription: string;
  diseaseDescription: string;
  dxid: number | null;
  fdbdx: string | null;
}

export interface FdbIndicationsResponse {
  din: string;
  drugName: string;
  gcn: number;
  indications: FdbIndication[];
}

// --- Dosage ---

export interface FdbDosageRange {
  routeOfAdmin: string;
  routeDescription: string;
  dosageType: string;
  dosageTypeDescription: string;
  ageRange: { lowDays: number | null; highDays: number | null };
  lowDosePerDay: number | null;
  highDosePerDay: number | null;
  maxDosePerDay: number | null;
  maxAmountPerSingleDose: number | null;
}

export interface FdbDailyDosing {
  ageGroupType: string;
  ageRange: { minDays: number | null; maxDays: number | null };
  minDailyDose: { qty: number | null; units: string | null } | null;
  maxDailyDose: { qty: number | null; units: string | null } | null;
  doNotExceed: { qty: number | null; units: string | null } | null;
}

export interface FdbDosageResponse {
  din: string;
  drugName: string;
  gcn: number;
  dosageRanges: FdbDosageRange[];
  dailyDosing: FdbDailyDosing[];
}

// --- Ingredients ---

export interface FdbIngredient {
  hicSeqn: number;
  description: string;
  isRelated: string | boolean;
  ingredient: string | null;
  baseIngredient: string | null;
  therapeuticClassification: string | null;
  pharmacologicalClassification: string | null;
}

export interface FdbIngredientsResponse {
  din: string;
  drugName: string;
  gcn: number;
  ingredients: FdbIngredient[];
}

// --- DUR Patient Screen (drug-drug interactions + full screen) ---

export interface FdbDrugRef {
  din: string;
  name: string;
  gcn: number;
  genericName: string;
  brandName: string;
  strength: string;
}

export interface FdbDrugInteraction {
  drug1: FdbDrugRef;
  drug2: FdbDrugRef;
  ddiCodex: number;
  description: string;
  severity: { level: number; description: string };
  clinicalEffect: { code: string; text: string };
  monographId: number;
}

export interface FdbDuplicateTherapy {
  therapyClass: string;
  therapyClassDetail: string;
  drugs: { din: string; name: string; gcn: number }[];
}

export interface FdbContraindication {
  drug: { din: string; name: string; gcn: number };
  disease: string;
  severityLevel: number;
  fdbdx: string;
  dxid: number;
  reference: string;
}

export interface FdbPatientMedication {
  din: string;
  drugName: string;
  rxNumber: string;
}

export interface FdbDurScreenResponse {
  drugDrugInteractions: FdbDrugInteraction[];
  duplicateTherapies: FdbDuplicateTherapy[];
  drugDiseaseContraindications: FdbContraindication[];
  warnings: FdbWarningsResponse[];
  precautions: FdbPrecautionsResponse[];
  screenedAt: string;
  drugCount: number;
  interactionCount: number;
  duplicateTherapyCount: number;
  contraindicationCount: number;
  warningCount: number;
  executionTimeMs: number;
  unresolvedDins: string[];
  patientId?: string;
  medicationCount?: number;
  medications?: FdbPatientMedication[];
  message?: string;
}

// --- Monograph Bundle (all sections in one request) ---

export interface FdbMonographBundle {
  warnings: FdbWarningsResponse | null;
  counseling: FdbCounselingResponse | null;
  sideEffects: FdbSideEffectsResponse | null;
  foodInteractions: FdbFoodInteractionsResponse | null;
  precautions: FdbPrecautionsResponse | null;
  patientEducation: FdbPatientEducationResponse | null;
  indications: FdbIndicationsResponse | null;
  dosage: FdbDosageResponse | null;
  ingredients: FdbIngredientsResponse | null;
}

// --- Service Class ---

class FdbService {
  private readonly BASE = '/api/fdb';

  /**
   * Fetches all monograph sections for a drug in a single request.
   * Replaces the 9 individual section calls. Each section is exactly the
   * payload its dedicated endpoint returns, or null if that section failed/404'd.
   */
  async getMonographBundle(
    din: string,
    opts?: { language?: string; patientAge?: number; patientAgeDays?: number },
  ): Promise<FdbMonographBundle> {
    const params: Record<string, string | number> = {};
    if (opts?.language) params.language = opts.language;
    if (opts?.patientAge !== undefined) params.patientAge = opts.patientAge;
    if (opts?.patientAgeDays !== undefined) params.patientAgeDays = opts.patientAgeDays;
    const response = await api.get(`${this.BASE}/drug/${din}/monograph-bundle`, { params });
    return response.data;
  }

  async getDrugCounseling(din: string): Promise<FdbCounselingResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/counseling`);
    return response.data;
  }

  async getDrugWarnings(din: string): Promise<FdbWarningsResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/warnings`);
    return response.data;
  }

  async getDrugSideEffects(din: string): Promise<FdbSideEffectsResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/side-effects`);
    return response.data;
  }

  async getDrugFoodInteractions(din: string): Promise<FdbFoodInteractionsResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/food-interactions`);
    return response.data;
  }

  async getDrugPrecautions(din: string, patientAge?: number): Promise<FdbPrecautionsResponse> {
    const params = patientAge !== undefined ? { patientAge } : {};
    const response = await api.get(`${this.BASE}/drug/${din}/precautions`, { params });
    return response.data;
  }

  async getPatientEducation(din: string): Promise<FdbPatientEducationResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/patient-education`);
    return response.data;
  }

  async screenDur(
    dins: string[],
    patientAge?: number,
    patientGender?: string,
  ): Promise<FdbDurScreenResponse> {
    const response = await api.post(`${this.BASE}/dur/screen`, {
      dins,
      patientAge,
      patientGender,
    });
    return response.data;
  }

  /**
   * PRX-137: the full DUR screen over the patient's own PMS medication list
   * (Kroll or Fillware). `pharmacyId` is the PATIENT's pharmacy: it is in the
   * path, where the server's path-pharmacy guard checks viewPatients (D12).
   */
  async screenPatientDur(pharmacyId: string, patientId: string): Promise<FdbDurScreenResponse> {
    const response = await api.post(
      `${this.BASE}/${encodeURIComponent(pharmacyId)}/dur/screen-patient/${encodeURIComponent(patientId)}`,
      {},
    );
    return response.data;
  }

  async getDdiMonograph(monographId: number): Promise<{ monographId: number; text: string }> {
    const response = await api.get(`${this.BASE}/interactions/drug-drug/monograph/${monographId}`);
    return response.data;
  }

  async getDrugInfo(din: string): Promise<FdbDrugInfo> {
    const response = await api.get(`${this.BASE}/drug/${din}`);
    return response.data;
  }

  async getDrugIndications(din: string): Promise<FdbIndicationsResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/indications`);
    return response.data;
  }

  async getDrugDosage(din: string, patientAgeDays?: number): Promise<FdbDosageResponse> {
    const params = patientAgeDays !== undefined ? { patientAgeDays } : {};
    const response = await api.get(`${this.BASE}/drug/${din}/dosage`, { params });
    return response.data;
  }

  async getDrugIngredients(din: string): Promise<FdbIngredientsResponse> {
    const response = await api.get(`${this.BASE}/drug/${din}/ingredients`);
    return response.data;
  }

  async getClinicalInteractions(params: {
    pharmacyId: string;
    patientId: string;
    currentDins: string[];
    currentRxNumber?: string;
    patientAge?: number;
    patientGender?: string;
  }): Promise<import('../types/clinicalInteractions.types').ClinicalInteractionsResponse> {
    const response = await api.post(`${this.BASE}/clinical-interactions`, params);
    return response.data;
  }

  /**
   * GP-1135 typeahead: coded allergen suggestions from FDB's allergy pick list
   * (the same concept vocabulary Kroll's own PatAlg records use — see
   * fdb.service.ts enrichAllergyDescriptions). Entries flagged
   * reverse-lookup-only are lookup aliases, not pickable concepts.
   */
  async searchAllergens(q: string, limit = 20): Promise<FdbAllergenPick[]> {
    const response = await api.get(`${this.BASE}/allergens/search`, { params: { q, limit } });
    return ((response.data || []) as FdbAllergenPick[]).filter((a) => !a.reverseLookupOnly);
  }

  /** GP-1135 typeahead: coded medical-condition suggestions (FDB FDBDX vocabulary). */
  async searchConditions(q: string, limit = 20): Promise<FdbConditionPick[]> {
    const response = await api.get(`${this.BASE}/conditions/search`, { params: { q, limit } });
    return (response.data || []) as FdbConditionPick[];
  }
}

export default new FdbService();
